import { Maximize2, Minimize2, X } from "lucide-react";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { App_Url_New } from "../common/static";
import FormLayout from "../legacy/components/ui/FormLayout";
import { closeModal } from "../redux/reducers/uiSlice";
import { useAppSelector } from "../redux/store/hooks";
import ChatBotRender from "./ChatBotRender";
import { CloseButton } from "./CloseButton";

const Modal = () => {
  const [chatBotSize, setChatBotSize] = useState<"normal" | "expanded">("normal");
  const { status, name, title, children, className, header, outerClassName, form, data, url } =
    useAppSelector((state) => state.ui);
  const user = useAppSelector((state) => state.auth);
  const onSubmit = form?.onSubmit;
  const defaultValues = form?.defaultValues;
  const submitText = form?.submitText;
  const dispatch = useDispatch();
  const isForm = typeof onSubmit === "function";
  const isChatBotModal = name === App_Url_New.PopupKeys.OPEN_CHAT_BOT;
  const chatBotUrl = (data?.iframeUrl || data?.url || url || "") as string;
  const isAuthenticated = Boolean(user.user?.access_token);

  useEffect(() => {
    if (status === "hide") {
      setChatBotSize("normal");
    }
  }, [status]);

  if (status === "hide") return null;

  if (isChatBotModal) {
    const isExpanded = chatBotSize === "expanded";
    const sideClass = isAuthenticated ? "left-4 md:left-6" : "right-4 md:right-6";
    const sizeClass = isExpanded
      ? "h-[calc(100dvh-2rem)] w-[min(960px,calc(100vw-2rem))]"
      : "h-[min(70dvh,680px)] w-[min(420px,calc(100vw-2rem))]";

    return ReactDOM.createPortal(
      <div
        id={`modal-${name}`}
        className="fixed inset-0 z-999 pointer-events-none"
        data-modal-name={name}
      >
        <div
          role="dialog"
          aria-label="Chat assistant"
          className={`pointer-events-auto absolute bottom-4 overflow-hidden rounded-2xl border border-theme-border-strong bg-[#0C1628] shadow-2xl transition-[width,height] duration-300 ${sideClass} ${sizeClass}`}
        >
          <div className="absolute right-3 top-4 z-10 flex items-center gap-1">
            <button
              type="button"
              onClick={() => setChatBotSize(isExpanded ? "normal" : "expanded")}
              aria-label={isExpanded ? "Restore chatbot size" : "Expand chatbot"}
              className="cursor-pointer rounded-full bg-black/40 p-1.5 text-theme-text-placeholder transition-colors hover:bg-black/60 hover:text-theme-text-inverse"
            >
              {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => {
                setChatBotSize("normal");
                dispatch(closeModal());
              }}
              aria-label="Close chatbot"
              className="cursor-pointer rounded-full bg-black/40 p-1.5 text-theme-text-placeholder transition-colors hover:bg-black/60 hover:text-theme-danger"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="h-full w-full">
            <ChatBotRender iframeUrl={chatBotUrl} />
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div id={`modal-${name}`} className="fixed inset-0 z-999" data-modal-name={name}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/10 backdrop-blur-sm `}
        onClick={() => dispatch(closeModal())}
      />

      {/* Modal Wrapper */}
      <div className={`relative flex min-h-screen justify-center items-center ${outerClassName}`}>
        <div
          className={`w-full max-h-[90vh] bg-theme-surface shadow-xl rounded-2xl flex flex-col animate-in fade-in zoom-in-95 p-3 ${className}`}
        >
          {/* Header */}
          <div className="relative mt-2 flex shrink-0 items-center justify-between px-5">
            <h3 className="text-xl font-semibold text-theme-text mb-2">{title}</h3>
            {header && <div>{header}</div>}
            <CloseButton onClick={() => dispatch(closeModal())} />
          </div>

          {/* Body */}
          {isForm ? (
            <FormLayout submitText={submitText} defaultValues={defaultValues} onSubmit={onSubmit}>
              <div className="px-5 py-4 text-theme-text-muted overflow-y-auto thin-scrollbar flex-1 min-h-0">
                {children}
              </div>
            </FormLayout>
          ) : (
            <div className="px-5 py-4 text-theme-text-muted overflow-y-auto thin-scrollbar flex-1 min-h-0">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
